/**
 * Create Task Modal
 * Allows Support Admins and Agents to manually create tasks
 */

import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { XMarkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import {
  createManualTask,
  getAgents,
  getAgentByUserId,
} from "../../services/supportTasksDb.service";
import {
  searchContacts,
  type ContactPickerResult,
} from "../../services/contacts.service";
import {
  contactPickerCanSearch,
  contactPickerStatus,
} from "../../utils/contactPickerStatus";
import type {
  SupportTaskType,
  SupportAgent,
} from "../../types/supportTasks.types";
import { groupedTaskTypeOptions } from "../../types/supportTasks.types";
import type { Contact } from "../../types/contact.types";
import { useAuth } from "../../contexts/AuthContext";
import { UserRole } from "../../types/auth.types";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";

interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  parentTaskId?: string;
  parentTaskNumber?: number;
  prefilledContact?: Contact;
}

const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  parentTaskId,
  parentTaskNumber,
  prefilledContact,
}) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const searchRef = useRef<HTMLDivElement>(null);

  // Role checks
  const isSupportAgent = user?.role === UserRole.SUPPORT_AGENT;
  const isSupportAdmin =
    user?.role === UserRole.SUPPORT_ADMIN ||
    user?.role === UserRole.SITE_ADMIN ||
    user?.role === UserRole.SUPER_ADMIN;

  // Form state — seed contact from prefilledContact if provided (follow-up mode)
  const [selectedContact, setSelectedContact] =
    useState<ContactPickerResult | null>(prefilledContact ?? null);
  const [contactSearch, setContactSearch] = useState("");
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<string>("");
  const [taskType, setTaskType] = useState<SupportTaskType>("general");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");

  // Get current user's agent profile (for Support Agent role)
  const { data: currentAgentProfile } = useQuery({
    queryKey: ["support-agent-profile", user?.id],
    queryFn: () => getAgentByUserId(user!.id),
    enabled: isSupportAgent && !!user?.id && isOpen,
  });

  // Get all agents (for Support Admin role)
  const { data: agents = [] } = useQuery({
    queryKey: ["support-agents"],
    queryFn: getAgents,
    enabled: isSupportAdmin && isOpen,
  });

  // Search contacts
  const {
    data: contactResults = [],
    isFetching: contactSearching,
    isError: contactSearchFailed,
  } = useQuery({
    queryKey: ["contact-search", contactSearch],
    queryFn: () => searchContacts(contactSearch),
    enabled: contactPickerCanSearch(contactSearch),
    // One retry only: a failing search should say so, not spin for ~7 s.
    retry: 1,
  });

  // Auto-select agent for Support Agent role
  useEffect(() => {
    if (isSupportAgent && currentAgentProfile) {
      setSelectedAgentId(currentAgentProfile.id);
    }
  }, [isSupportAgent, currentAgentProfile]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setShowContactDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedContact(prefilledContact ?? null);
      setContactSearch("");
      setTaskType("general");
      setDueDate(new Date().toISOString().split("T")[0]);
      setNotes("");
      setError("");
      if (isSupportAgent && currentAgentProfile) {
        setSelectedAgentId(currentAgentProfile.id);
      } else {
        setSelectedAgentId("");
      }
    }
  }, [isOpen, isSupportAgent, currentAgentProfile, prefilledContact]);

  // Create task mutation
  const createMutation = useMutation({
    mutationFn: createManualTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["support-agents"] });
      queryClient.invalidateQueries({ queryKey: ["agent-tasks"] });
      onSuccess?.();
      onClose();
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to create task");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!selectedContact) {
      setError("Please select a contact");
      return;
    }

    if (!selectedAgentId) {
      setError("Please select an agent");
      return;
    }

    createMutation.mutate({
      agentId: selectedAgentId,
      contactId: selectedContact.id,
      taskType,
      dueDate,
      notes: notes || undefined,
      createdBy: user!.id,
      parentTaskId: parentTaskId || undefined,
    });
  };

  const handleContactSelect = (contact: ContactPickerResult) => {
    setSelectedContact(contact);
    setContactSearch("");
    setShowContactDropdown(false);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">
            {parentTaskNumber
              ? `Create Follow-up (Task #${parentTaskNumber})`
              : "Create Task"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Contact Search */}
          <div ref={searchRef} className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact <span className="text-red-500">*</span>
            </label>
            {selectedContact ? (
              <div className="flex items-center justify-between p-3 bg-gray-50 border rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">
                    {selectedContact.first_name} {selectedContact.last_name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {selectedContact.email || selectedContact.phone_number}
                  </p>
                </div>
                {!prefilledContact && (
                  <button
                    type="button"
                    onClick={() => setSelectedContact(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={contactSearch}
                    onChange={(e) => {
                      setContactSearch(e.target.value);
                      setShowContactDropdown(true);
                    }}
                    onKeyDown={preventEnterSubmit}
                    onFocus={() => setShowContactDropdown(true)}
                    placeholder="Search by name, email, phone or membership number..."
                    className="w-full pl-9 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                {showContactDropdown && contactResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {contactResults.map((contact) => (
                      <button
                        key={contact.id}
                        type="button"
                        onClick={() => handleContactSelect(contact)}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b last:border-b-0"
                      >
                        <p className="font-medium text-gray-900">
                          {contact.first_name} {contact.last_name}
                        </p>
                        <p className="text-sm text-gray-500">
                          {contact.email || contact.phone_number}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
                {showContactDropdown &&
                  contactSearch.trim().length > 0 &&
                  contactResults.length === 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-sm text-gray-500">
                      {contactPickerStatus(
                        contactSearch,
                        contactSearching,
                        contactSearchFailed,
                      )}
                    </div>
                  )}
              </>
            )}
          </div>

          {/* Agent Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assign To <span className="text-red-500">*</span>
            </label>
            {isSupportAgent ? (
              // Support Agent can only assign to themselves
              <div className="p-3 bg-gray-50 border rounded-lg">
                <p className="font-medium text-gray-900">
                  {currentAgentProfile?.name || "Loading..."}
                </p>
                <p className="text-xs text-gray-500">
                  Support Agents can only assign tasks to themselves
                </p>
              </div>
            ) : (
              // Support Admin can assign to anyone
              <select
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">Select an agent...</option>
                {agents.map((agent: SupportAgent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name} (
                    {agent.pending_count + agent.in_progress_count} active
                    tasks)
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Task Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Task Type <span className="text-red-500">*</span>
            </label>
            <select
              value={taskType}
              onChange={(e) => setTaskType(e.target.value as SupportTaskType)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {groupedTaskTypeOptions().map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Due Date
            </label>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Add any notes about this task..."
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isPending ? "Creating..." : "Create Task"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTaskModal;
